import { useCallback } from "react";

import toast from "react-hot-toast";

import {
  GroupFragmentDoc,
  useFinaliseGroupMutation,
  useGroupUpdatedSubscription,
  useJoinGroupMutation,
  useLeaveGroupMutation,
  WorkGroupDocument,
} from "@/generated/graphql";

/**
 * Hook to join a group in self enrolment groups.
 */
export function useJoinGroup(assessmentId: string) {
  const [joinGroupMutation] = useJoinGroupMutation();
  return useCallback(
    (groupId: string, onCompleted?: () => void) => {
      joinGroupMutation({
        variables: {
          assessmentId,
          groupId,
        },
        update: (cache) => {
          cache.evict({
            fieldName: "studentWorkGroup",
            args: { assessmentId },
          });
          cache.gc();
        },
        onCompleted: () => {
          onCompleted?.();
        },
        onError: (error) => {
          toast.error(error.message);
        },
      });
    },
    [joinGroupMutation, assessmentId]
  );
}

/**
 * Hook to leave a group in self enrolment groups.
 */
export function useLeaveGroup(assessmentId: string) {
  const [leaveGroupMutation] = useLeaveGroupMutation();
  return useCallback(
    (groupId: string, onCompleted?: () => void) => {
      leaveGroupMutation({
        variables: {
          assessmentId,
          groupId,
        },
        update: (cache) => {
          cache.evict({
            fieldName: "studentWorkGroup",
            args: { assessmentId },
          });
          cache.gc();
        },
        onCompleted: () => {
          onCompleted?.();
        },
        onError: () => {
          toast.error("Error in Leaving Group");
        },
      });
    },
    [leaveGroupMutation, assessmentId]
  );
}

/**
 * Hook to finalise the student's own group.
 */
export function useFinaliseGroup(assessmentId: string) {
  const [finaliseGroupMutation] = useFinaliseGroupMutation();
  return useCallback(
    (onCompleted?: () => void) => {
      finaliseGroupMutation({
        variables: {
          assessmentId,
        },
        refetchQueries: [
          { query: WorkGroupDocument, variables: { assessmentId } },
        ],
        onCompleted: () => {
          onCompleted?.();
        },
        onError: (error) => {
          toast.error(error.message);
        },
      });
    },
    [finaliseGroupMutation, assessmentId]
  );
}

/**
 * Subscription to update the group list.
 */
export function useGroupUpdateSubscription(assessmentId: string) {
  return useGroupUpdatedSubscription({
    variables: { assessmentId },
    onData: ({ client, data: { data } }) => {
      if (data && data.groupUpdated) {
        const groupId = data.groupUpdated.id;
        client.writeFragment({
          id: `Group:${groupId}`,
          fragment: GroupFragmentDoc,
          fragmentName: "Group",
          data: data.groupUpdated,
        });
      }
    },
  });
}
